// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Material UI theme factory built from the semantic design tokens.
 *
 * ADR-0011 fixes Material UI as the approved component library. This factory is
 * the single place where tokens become MUI theme values, so feature code never
 * hard-codes a colour, radius, or spacing literal.
 *
 * Accessibility behaviour encoded here rather than per component
 * (ADR-0042, `docs/10-TESTING.md` §30):
 *
 * - a visible focus indicator that survives mouse interaction;
 * - a minimum interactive target size;
 * - honouring `prefers-reduced-motion`; and
 * - relative type sizing so browser zoom and reflow keep working.
 */

import { createTheme, type Theme } from '@mui/material/styles';

import {
  darkColors,
  focusRingWidthPx,
  fontFamily,
  lightColors,
  minimumTargetSizePx,
  radii,
  spacingUnit,
  typeScale,
  type ColorScheme,
} from '../design-system/tokens';

/** Supported theme modes. */
export type ThemeMode = 'light' | 'dark';

/** Resolve the semantic colour scheme for a mode. */
export function colorsForMode(mode: ThemeMode): ColorScheme {
  return mode === 'dark' ? darkColors : lightColors;
}

/**
 * Build the application theme for a mode.
 *
 * @param mode - Requested theme mode.
 * @returns A configured Material UI theme.
 */
export function createAppTheme(mode: ThemeMode): Theme {
  const colors = colorsForMode(mode);

  return createTheme({
    palette: {
      mode,
      background: { default: colors.background, paper: colors.surface },
      text: { primary: colors.onSurface, secondary: colors.onSurfaceMuted },
      primary: { main: colors.primary, contrastText: colors.onPrimary },
      error: { main: colors.danger, contrastText: colors.onDanger },
      warning: { main: colors.warning, contrastText: colors.onWarning },
      success: { main: colors.success, contrastText: colors.onSuccess },
      info: { main: colors.info, contrastText: colors.onInfo },
      divider: colors.border,
    },
    shape: { borderRadius: radii.md },
    spacing: spacingUnit,
    typography: {
      fontFamily,
      htmlFontSize: 16,
      body1: { fontSize: `${String(typeScale.body)}rem` },
      body2: { fontSize: `${String(typeScale.bodySmall)}rem` },
      h1: { fontSize: `${String(typeScale.heading1)}rem`, fontWeight: 600 },
      h2: { fontSize: `${String(typeScale.heading2)}rem`, fontWeight: 600 },
      h3: { fontSize: `${String(typeScale.heading3)}rem`, fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*:focus-visible': {
            outline: `${String(focusRingWidthPx)}px solid ${colors.focusRing}`,
            outlineOffset: '2px',
          },
          '@media (prefers-reduced-motion: reduce)': {
            '*': {
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '0.01ms !important',
              scrollBehavior: 'auto !important',
            },
          },
        },
      },
      MuiButtonBase: {
        defaultProps: { disableRipple: true },
        styleOverrides: {
          root: {
            minHeight: minimumTargetSizePx,
            minWidth: minimumTargetSizePx,
          },
        },
      },
      MuiLink: {
        defaultProps: { underline: 'always' },
      },
    },
  });
}
